import { updateSession } from '../api/sessions';
import { getUserById, getUserPermissions } from '../api/users';
import {
  resetUser,
  selectUserId,
  setSessionExchangeErrorStatus,
  setUser,
  setUserPermissions,
} from '../containers/Login/LoginSlice';
import { clearTenant } from '../containers/Organizations/OrganizationsSlice';
import {
  ensureTenant,
  honourTenantIdQueryParam,
  registerActiveTenantSession,
} from '../containers/Organizations/OrganizationsSlice/loaderHelpers';
import { store } from '../store';
import { cookieToJwtExchange } from '../utils/cookieToJwtExchange';
import { safeParseJwtPayload } from '../utils/jwt';
import { getItem } from '../utils/storage';
import { OpenFeature } from '@openfeature/web-sdk';
import { tryUpdatePushToken } from '../containers/Sidebar/actionCreators';
import { ensureFeatureFlagsProvider } from '../featureFlags';

export type EnsureUserResult = { ok: true; userId: number } | { ok: false; response: Response };

export type ExchangeUlfSessionTokensResult =
  | { ok: true; exchanged: boolean }
  | { ok: false; response: Response };

/** Ensures the authenticated user is loaded into Redux. Used by route loaders. */
export async function ensureUser({
  tenantId,
  shouldRefresh = false,
}: {
  tenantId: number;
  shouldRefresh?: boolean;
}): Promise<EnsureUserResult> {
  if (!shouldRefresh) {
    const stateUserId = selectUserId(store.getState());
    if (stateUserId) return { ok: true, userId: stateUserId };
  }

  const accessToken = getItem('coreApiAccessToken');
  if (!accessToken) {
    return { ok: false, response: new Response('Failed to fetch user', { status: 401 }) };
  }

  const tokenUserId = Number(safeParseJwtPayload(accessToken)?.sub);
  if (!tokenUserId) {
    return { ok: false, response: new Response('Failed to fetch user', { status: 401 }) };
  }

  const userResponse = await getUserById({ userId: tokenUserId, tenantId });
  if (userResponse.error) {
    return {
      ok: false,
      response: new Response('Failed to fetch user', {
        status: userResponse.error.error?.httpStatus ?? 500,
      }),
    };
  }

  store.dispatch(setUser(userResponse.data.data));
  return { ok: true, userId: userResponse.data.data.id };
}

/**
 * Returns `true` when an ULF session exchange actually occurred on this
 * navigation (i.e. the URL carried fresh `sessionId`+`refreshToken` params and
 * `updateSession` succeeded). Callers use this to decide whether to run the
 * post-exchange tenant/user registration flow.
 */
export async function exchangeUlfSessionTokens(
  request: Request
): Promise<ExchangeUlfSessionTokensResult> {
  const url = new URL(request.url);
  const sessionId = url.searchParams.get('sessionId');
  const refreshToken = url.searchParams.get('refreshToken');

  if (!sessionId || !refreshToken) return { ok: true, exchanged: false };

  const sessionResult = await updateSession({ sessionId, refreshToken });
  if (sessionResult.error) {
    return {
      ok: false,
      response: new Response('Session refresh failed', {
        status: sessionResult.error.error?.httpStatus ?? 500,
      }),
    };
  }
  return { ok: true, exchanged: true };
}

async function runWithoutThrowing(
  message: string,
  context: Record<string, unknown>,
  fn: () => Promise<unknown>
): Promise<void> {
  try {
    await fn();
  } catch (error) {
    console.error(message, { ...context, error });
  }
}

/** Connects the flag provider before tenant resolution reads flags. Never throws. */
export async function ensureFeatureFlagsReady(): Promise<void> {
  await runWithoutThrowing('Feature flag provider setup failed', {}, async () => {
    const result = await ensureFeatureFlagsProvider();
    if (!result.ok) {
      throw result.error instanceof Error
        ? result.error
        : new Error('Feature flag provider setup failed');
    }
  });
}

/** Scopes flag evaluation to the logged-in user. Never throws. */
export async function alignFeatureFlagsForLogin({
  userId,
  tenantId,
}: {
  userId: number;
  tenantId: number;
}): Promise<void> {
  await runWithoutThrowing('Feature flag context update failed', { userId, tenantId }, () =>
    OpenFeature.setContext({
      userId: String(userId),
      tenantId: String(tenantId),
      organizationId: String(tenantId),
    })
  );
}

export type PublicRouteLoaderDeps = {
  exchangeUlfSessionTokens: typeof exchangeUlfSessionTokens;
  cookieToJwtExchange: typeof cookieToJwtExchange;
  ensureFeatureFlagsReady: typeof ensureFeatureFlagsReady;
  ensureTenant: typeof ensureTenant;
  ensureUser: typeof ensureUser;
  alignFeatureFlagsForLogin: typeof alignFeatureFlagsForLogin;
  registerActiveTenantSession: typeof registerActiveTenantSession;
  honourTenantIdQueryParam: typeof honourTenantIdQueryParam;
};

const defaultPublicRouteLoaderDeps: PublicRouteLoaderDeps = {
  exchangeUlfSessionTokens,
  cookieToJwtExchange,
  ensureFeatureFlagsReady,
  ensureTenant,
  ensureUser,
  alignFeatureFlagsForLogin,
  registerActiveTenantSession,
  honourTenantIdQueryParam,
};

/**
 * Public-route loader body. Always rehydrates Redux identity from the cached
 * tokens so previously logged-in users land on `/dashboard` after a cold start;
 * only consumes the add-org marker (`registerActiveTenantSession`) when an ULF
 * exchange actually happened on this navigation.
 *
 * `deps` exists for unit tests; production always uses the defaults.
 */
export async function runPublicRouteLoader(
  request: Request,
  deps: PublicRouteLoaderDeps = defaultPublicRouteLoaderDeps
): Promise<true> {
  store.dispatch(setSessionExchangeErrorStatus(null));
  const exchangeResult = await deps.exchangeUlfSessionTokens(request);
  if (!exchangeResult.ok) {
    store.dispatch(setSessionExchangeErrorStatus(exchangeResult.response.status));
    return true;
  }

  if (exchangeResult.exchanged) {
    store.dispatch(clearTenant());
    store.dispatch(resetUser());
  }

  // Invoking cookieToJwtExchange after exchangeUlfSessionTokens since cookieToJwtExchange is a no-op when the user has JWT (and we can avoid redundant API calls)
  await deps.cookieToJwtExchange();

  try {
    await deps.ensureFeatureFlagsReady();

    const tenantResult = await deps.ensureTenant({ shouldRefresh: true });
    if (!tenantResult.ok) return true;
    const userResult = await deps.ensureUser({
      tenantId: tenantResult.tenantId,
      shouldRefresh: true,
    });
    if (!userResult.ok) return true;

    await deps.alignFeatureFlagsForLogin({
      userId: userResult.userId,
      tenantId: tenantResult.tenantId,
    });

    if (exchangeResult.exchanged) {
      await deps.registerActiveTenantSession(tenantResult.tenantId);
    }
    await deps.honourTenantIdQueryParam(request);
  } catch {
    // We're intentionally not handling errors on this level so each route can handle its own errors
  }

  return true;
}

export type AuthRouteLoaderDeps = {
  cookieToJwtExchange: typeof cookieToJwtExchange;
  ensureFeatureFlagsReady: typeof ensureFeatureFlagsReady;
  ensureTenant: typeof ensureTenant;
  ensureUser: typeof ensureUser;
  getUserPermissions: typeof getUserPermissions;
  alignFeatureFlagsForLogin: typeof alignFeatureFlagsForLogin;
  registerActiveTenantSession: typeof registerActiveTenantSession;
  honourTenantIdQueryParam: typeof honourTenantIdQueryParam;
  tryUpdatePushToken: typeof tryUpdatePushToken;
  setUserPermissionsAction: typeof setUserPermissions;
};

const defaultAuthRouteLoaderDeps: AuthRouteLoaderDeps = {
  cookieToJwtExchange,
  ensureFeatureFlagsReady,
  ensureTenant,
  ensureUser,
  getUserPermissions,
  alignFeatureFlagsForLogin,
  registerActiveTenantSession,
  honourTenantIdQueryParam,
  tryUpdatePushToken,
  setUserPermissionsAction: setUserPermissions,
};

/**
 * Auth-route loader body. Covers reload and deep-link entry where the public
 * loader does not run. `deps` exists for unit tests; production uses defaults.
 */
export async function runAuthRouteLoader(
  request: Request,
  deps: AuthRouteLoaderDeps = defaultAuthRouteLoaderDeps
): Promise<true> {
  try {
    await deps.cookieToJwtExchange();
    await deps.ensureFeatureFlagsReady();

    const tenantResult = await deps.ensureTenant({ shouldRefresh: true });
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loader convention
    if (!tenantResult.ok) throw tenantResult.response;
    const { tenantId } = tenantResult;

    const userResult = await deps.ensureUser({ tenantId, shouldRefresh: true });
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loader convention
    if (!userResult.ok) throw userResult.response;
    const { userId } = userResult;

    const userPermissions = await deps.getUserPermissions({ userId, tenantId });
    if (userPermissions.error) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loader convention
      throw new Response('Failed to fetch user', {
        status: userPermissions.error.error?.httpStatus ?? 500,
      });
    }
    store.dispatch(deps.setUserPermissionsAction(userPermissions.data.data));

    await deps.alignFeatureFlagsForLogin({ userId, tenantId });

    await deps.registerActiveTenantSession(tenantId);
    await deps.honourTenantIdQueryParam(request);

    await deps.tryUpdatePushToken();
  } catch {
    // We're intentionally not handling errors on this level so each route can handle its own errors
  }

  return true;
}
